import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Megaphone, Plus, Trash2, Pencil, Info, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', message: '', type: 'update', duration_days: 20 });

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = () => {
    fetch(`${API_URL}/api/admin/announcements`, { headers })
      .then(r => r.json())
      .then(data => setItems(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!form.message.trim()) { toast.error('Nachricht ist erforderlich'); return; }
    try {
      if (editing) {
        await fetch(`${API_URL}/api/admin/announcements/${editing}`, { method: 'PUT', headers, body: JSON.stringify(form) });
        toast.success('Hinweis aktualisiert');
      } else {
        await fetch(`${API_URL}/api/admin/announcements`, { method: 'POST', headers, body: JSON.stringify(form) });
        toast.success('Hinweis erstellt');
      }
      setForm({ title: '', message: '', type: 'update', duration_days: 20 });
      setEditing(null);
      load();
    } catch { toast.error('Fehler beim Speichern'); }
  };

  const handleDelete = async (id) => {
    await fetch(`${API_URL}/api/admin/announcements/${id}`, { method: 'DELETE', headers });
    toast.success('Hinweis gelöscht');
    load();
  };

  const handleToggle = async (id, active) => {
    await fetch(`${API_URL}/api/admin/announcements/${id}`, { method: 'PUT', headers, body: JSON.stringify({ active: !active }) });
    load();
  };

  const startEdit = (item) => {
    setEditing(item.id);
    setForm({ title: item.title, message: item.message, type: item.type, duration_days: item.duration_days });
  };

  const typeIcons = { info: Info, update: Megaphone, warning: AlertTriangle };
  const typeLabels = { info: 'Info', update: 'Update', warning: 'Warnung' };
  const typeColors = {
    info: 'bg-blue-50 text-blue-700 border-blue-200',
    update: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-announcements">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Hinweise</h1>
        <p className="text-sm text-muted-foreground">Hinweise und Updates auf der Startseite verwalten</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">{editing ? 'Hinweis bearbeiten' : 'Neuen Hinweis erstellen'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Titel (optional)</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="z.B. System-Update" data-testid="announcement-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Typ</Label>
                <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="announcement-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="update">Update</SelectItem>
                    <SelectItem value="warning">Warnung</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Dauer (Tage)</Label>
                <Input type="number" min={1} max={365} value={form.duration_days} onChange={e => setForm(f => ({ ...f, duration_days: parseInt(e.target.value) || 20 }))} data-testid="announcement-days" />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Nachricht</Label>
            <textarea
              className="w-full border border-input rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              rows={3}
              value={form.message}
              onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              placeholder="Beschreiben Sie das Update oder den Hinweis..."
              maxLength={1000}
              data-testid="announcement-message"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleSave} className="gap-1.5" data-testid="announcement-save">
              {editing ? <Pencil className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
              {editing ? 'Aktualisieren' : 'Erstellen'}
            </Button>
            {editing && (
              <Button variant="ghost" onClick={() => { setEditing(null); setForm({ title: '', message: '', type: 'update', duration_days: 20 }); }}>Abbrechen</Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Alle Hinweise ({items.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Noch keine Hinweise erstellt.</p>
          ) : (
            <div className="space-y-3">
              {items.map(item => {
                const Icon = typeIcons[item.type] || Info;
                const isExpired = new Date(item.expires_at) < new Date();
                const daysLeft = Math.max(0, Math.ceil((new Date(item.expires_at) - new Date()) / 86400000));
                return (
                  <div key={item.id} className={`flex items-start gap-3 p-4 rounded-lg border ${isExpired || !item.active ? 'opacity-50 bg-slate-50' : 'bg-white'}`} data-testid={`announcement-item-${item.id}`}>
                    <Icon className="h-5 w-5 flex-shrink-0 mt-0.5 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {item.title && <span className="font-semibold text-sm">{item.title}</span>}
                        <Badge className={`text-[10px] ${typeColors[item.type]}`}>{typeLabels[item.type]}</Badge>
                        {isExpired && <Badge variant="secondary" className="text-[10px]">Abgelaufen</Badge>}
                        {!item.active && <Badge variant="secondary" className="text-[10px]">Deaktiviert</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground">{item.message}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Erstellt: {formatDate(item.created_at)} · Ablauf: {formatDate(item.expires_at)} {!isExpired && `(${daysLeft} Tage)`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Switch checked={item.active} onCheckedChange={() => handleToggle(item.id, item.active)} data-testid={`toggle-${item.id}`} />
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => startEdit(item)} data-testid={`edit-${item.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive" onClick={() => handleDelete(item.id)} data-testid={`delete-${item.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
